import React, { useMemo } from "react";
import { Popover } from "antd";
import { useTranslation } from "react-i18next";

interface AudienceBadgesSectionProps {
  title: string;
  selectedType: string;
  values: string | string[];
}

export const AudienceBadgesSection: React.FC<AudienceBadgesSectionProps> = ({
  title,
  selectedType,
  values
}) => {
  const { t } = useTranslation();

  const normalizedValues = useMemo(() => {
    if (Array.isArray(values)) return values;
    if (typeof values === 'string' && values.trim().length > 0) return [values];
    return [];
  }, [values]);

  const typeSingular = useMemo(() => {
    if (selectedType === "groups") return "group";
    if (selectedType === "roles") return "role";
    if (selectedType === "individuals") return "individual";
    return "property";
  }, [selectedType]);

  const typePlural = useMemo(() => {
    if (selectedType === "groups") return "groups";
    if (selectedType === "roles") return "roles";
    if (selectedType === "individuals") return "individuals";
    return "properties";
  }, [selectedType]);

  const isAllSelected = useMemo(() => {
    if (normalizedValues.length === 0) return false;
    
    if (normalizedValues.length === 1 && normalizedValues[0]?.toLowerCase().startsWith("all ")) {
      return true;
    }

    if (selectedType === "properties" && normalizedValues.length >= 21) {
      return true;
    }

    if (selectedType === "roles" && normalizedValues.length >= 3) {
      return true;
    }

    return false;
  }, [normalizedValues, selectedType]);

  const allDisplayLabel = useMemo(() => {
    if (normalizedValues.length === 1 && normalizedValues[0]?.toLowerCase().startsWith("all ")) {
      return normalizedValues[0];
    }
    const capitalizedPlural = typePlural.charAt(0).toUpperCase() + typePlural.slice(1);
    return `All ${capitalizedPlural}`;
  }, [normalizedValues, typePlural]);

  if (selectedType === "organization") {
    return (
      <div className="card-step">
        <div className="card-step-title">{title}</div>
        <div className="audience-badge-wrapper single-badge">
          <div className="audience-pill-item">
            <span className="audience-pill-text">
              {t('phishingManager.campaignSetup.step3.organization.entireOrganization')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (isAllSelected) {
    return (
      <div className="card-step">
        <div className="card-step-title">{title}</div>
        <div className="audience-badge-wrapper single-badge">
          <div className="audience-pill-item">
            <span className="audience-pill-text capitalize-text">
              {allDisplayLabel}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const showMoreBadge = normalizedValues.length > 5;
  const initialValues = showMoreBadge ? normalizedValues.slice(0, 5) : normalizedValues;
  const remainingValues = showMoreBadge ? normalizedValues.slice(5) : [];
  const remainingCount = remainingValues.length;

  const popoverContent = (
    <div className="popover-badge-container">
      <div className="popover-badge-header">
        <div className="popover-title">
          {t('phishingManager.campaignSetup.step5.additional')} {typePlural}
        </div>
        <div className="popover-subtitle">
          {remainingCount} {remainingCount === 1 ? typeSingular : typePlural}
        </div>
      </div>

      <div className="popover-badge-list">
        {remainingValues.map((item, idx) => (
          <div key={idx} className="audience-pill-item">
            <span className="audience-pill-text">
              {item}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="card-step" style={{ position: 'relative' }}>
      <div className="card-step-title">{title}</div>
      <div className="audience-badge-wrapper">
        {initialValues.map((item, idx) => (
          <div key={idx} className="audience-pill-item">
            <span className="audience-pill-text">
              {item}
            </span>
          </div>
        ))}

        {showMoreBadge && (
          <Popover 
            content={popoverContent} 
            trigger="click" 
            placement="bottom"
            overlayClassName="custom-badge-popover"
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          >
            <div className="audience-pill-more">
              <span className="audience-pill-more-text">
                +{remainingCount} {t('phishingManager.campaignSetup.step5.more')}
              </span>
            </div>
          </Popover>
        )}
      </div>
    </div>
  );
};

export default AudienceBadgesSection;